import { useMemo } from 'react';
import { useBillingConfigs, useWarningAcks } from '../../hooks/api/budgetBillingConfig';
import { getRowCompleteness, isAckCovered, RowAckInfo, RowCompleteness } from './budgetCalculation';
import { useBudgetPeriodMonths } from './useBudgetPeriodMonths';

export interface RowStatusInfo {
    /** The row's completeness over the funded period, entered data only. */
    completeness: RowCompleteness;
    /** Set once the stored acknowledgements have loaded; until then the row is inert. */
    ackInfo?: RowAckInfo;
}

/**
 * CALL-816: ONE verdict per config, by config id — the icon, its tooltip and the acknowledgement dialog
 * all read this, so they cannot disagree.
 *
 * That is the point of computing it here rather than in the grid's row build. The status used to be
 * counted over the VIEW (the 12-month window or the date range) while the acknowledgement was counted
 * over the budget period, so a tooltip reading "0 / 12 months" opened a dialog listing 25: the user
 * resolved a record far wider than the one they had just been shown. Both now run over
 * useBudgetPeriodMonths — see there for why the axis is the funded months.
 *
 * Entered data only (`extrapolate: false`), also on purpose: an acknowledgement freezes real gaps, not
 * projections, so letting "Mit Extrapolation" move the traffic light would put a green check on a row
 * whose dialog still lists missing months. The toggle keeps its effect on the CELLS (the blue projected
 * values and the costs derived from them) — it just no longer changes the verdict, which matches the
 * export's entered-only Handlingdaten sheet.
 *
 * An empty period means nothing is entered anywhere yet, or the summary has not landed: no entry, so
 * the row renders no icon instead of claiming "no data" over a zero-length axis. `ackInfo` waits for the
 * acks query separately — an unloaded ack list is indistinguishable from "none", and a row that renders
 * unacknowledged while clickable lets a confirm overwrite the original acknowledger and timestamp (see
 * useWarningAcks).
 *
 * The configs come from the query rather than from a prop: the Overview hands its grid a FILTERED list
 * ("Inaktive ausblenden"), whose identity changes on every cell edit because the filter deliberately
 * keeps edited hidden rows — which put this whole pass, which walks every config over the whole period,
 * back into the edit path. The map is keyed by config id, so covering hidden rows too costs an entry
 * nobody looks up.
 */
export const useRowStatus = (versionId: number | undefined): Map<number, RowStatusInfo> => {
    const [billingConfigs] = useBillingConfigs(versionId);
    const periodMonths = useBudgetPeriodMonths(versionId);
    const { acks, isLoaded } = useWarningAcks(versionId);

    return useMemo(() => {
        const byConfig = new Map<number, RowStatusInfo>();
        if (!periodMonths.length) return byConfig;

        const acksByConfig = new Map(acks.map((ack) => [ack.billingConfigId, ack]));
        for (const config of billingConfigs) {
            const completeness = getRowCompleteness(config, periodMonths, false);
            if (!isLoaded) {
                byConfig.set(config.id, { completeness });
                continue;
            }

            const ack = acksByConfig.get(config.id);
            const canonicalMissing = completeness.missingMonths;
            byConfig.set(config.id, {
                completeness,
                ackInfo: {
                    ack,
                    canonicalMissing,
                    covered: Boolean(ack && isAckCovered(canonicalMissing, ack.missingMonths)),
                },
            });
        }
        return byConfig;
    }, [billingConfigs, periodMonths, acks, isLoaded]);
};
